import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Preference, SlackSettings } from "../../pages/Settings/types";
import { UserPreferenceItem } from "../../services/userPreferences";
import { GetPinnedProjectsResponse } from '../../types/apiResponse';

export const organizationSlice = createSlice({
    name: 'organization',
    initialState: {
        data: [] as any[],
        loading: false,
        loadingMore: false,
        hasMore: true,
        page: 1,
        perPage: 10,
        totalPages: 1,
        totalItems: 0,
        searchTerm: '',

        //combined API state (fetchRoadmapsAndProjects) - used by Work Items page
        workItems: [] as any[],
        workItemsLoading: false,
        workItemsLoadingMore: false,
        workItemsHasMore: true,
        workItemsPage: 1,
        workItemsPerPage: 10,
        workItemsTotalPages: 1,
        workItemsTotalItems: 0,
        workItemsSearchTerm: '',
        // Filter state for combined API (all prefixed with workItems)
        workItemsSearchType: 'all' as 'all' | 'roadmap' | 'project' | 'epic' | 'feature',
        workItemsSortBy: 'modified_at_desc' as string,
        workItemsSourceFilter: '' as string,
        workItemsQuarterFilter: [] as string[],
        workItemsPinnedFilter: false as boolean,

        orgdata: {},
        orgDetails: {} as any,
        skills: [] as any[],
        recentProjects: [] as any[],
        pinnedProjects: [] as GetPinnedProjectsResponse[],
        pinnedProjectsIds: [] as any[],
        skillLoading: false,
        recentProjectsLoading: false,
        skillError: null,
        error: null,
        orgUpdateError: null,
        recentProjectsError: null,
        orgUpdateSuccess: false,
        showSkillEdit: false,
        orgUpdateLoading: false,
        inviteUsersLoading: false,
        inviteUsersError: null,
        inviteUsersSuccess: false,
        jiraSettingsLoading: false,
        jiraSettingsSaveError: null,
        jiraSettingsSuccess: null,
        jiraSettingsError: null,
        asanaSettingsLoading: false,
        asanaSettingsSaveError: null,
        asanaSettingsSuccess: null,
        asanaSettingsError: null,
        projectReferenceLoading: false,
        projectReferenceError: null,
        projectReferenceData: [] as any[],
        jiraSettings: {},
        asanaSettings: {},
        onboardSettings: {},
        onboardSettingsLoaded: false,
        orgPreferences: [] as Preference[],
        orgPreferencesLoading: false,
        orgPreferencesError: null,
        orgWorkingDays: [] as any[],
        orgWorkingDaysLoading: false,
        azureSettingsLoading: false,
        azureSettingsSaveError: null,
        azureSettingsSuccess: null,
        azureSettingsError: null,
        azureSettings: {},
        mondaySettingsLoading: false,
        mondaySettingsSaveError: null,
        mondaySettingsSuccess: null,
        mondaySettingsError: null,
        mondaySettings: {},
        orgSchedule: [] as any[],
        orgScheduleLoading: false,
        orgScheduleError: null,
        showOrgScheduleEdit: false,
        activeIntegrationsLoading: false,
        activeIntegrations: [] as { integration_type: string, is_active: boolean }[],
        teams: [] as any[],
        teamsLoading: false,
        teamsError: null,
        teamsFetched: false,
        pendingInvites: [] as any[],
        pendingInvitesLoading: false,
        pendingInvitesError: null,
        revokeInviteLoading: false,
        revokeInviteError: null,
        revokeInviteSuccess: false,
        githubSettings: {},
        githubSettingsLoading: false,
        githubSettingsError: null,
        userPreferences: [] as UserPreferenceItem[],
        userPreferencesLoading: false,

        toleranceModes: [] as any[],
        toleranceModesLoading: false,
        toleranceModesError: null,
        slackSettingsLoading: false,
        slackSettings: null as SlackSettings | null,
        slackSettingsError: null as unknown,
        // Persisted long-running sync flags (cross-feature)
        azureStatesSyncRunning: false,
        jiraStatesSyncRunning: false
    },
    reducers: {
        setOrganizationData: (state, action) => {
            // Support both new API (items) and old API (projects) response formats
            state.data = action.payload.items || action.payload.projects || []
            state.totalItems = action.payload.total || 0
            state.totalPages = action.payload.pages || 1
            state.page = action.payload.page || 1
            state.perPage = action.payload.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        appendOrganizationData: (state, action) => {
            // Support both new API (items) and old API (projects) response formats
            state.data = [...state.data, ...(action.payload.items || action.payload.projects || [])]
            state.totalItems = action.payload.total || 0
            state.totalPages = action.payload.pages || 1
            state.page = action.payload.page || 1
            state.perPage = action.payload.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        // Work Items reducers for combined API (fetchRoadmapsAndProjects)
        setWorkItemsData: (state, action) => {
            state.workItems = action.payload.items || []
            state.workItemsTotalItems = action.payload.total || 0
            state.workItemsTotalPages = action.payload.pages || 1
            state.workItemsPage = action.payload.page || 1
            state.workItemsPerPage = action.payload.per_page || 10
            state.workItemsHasMore = state.workItemsPage < state.workItemsTotalPages
        },
        appendWorkItemsData: (state, action) => {
            const newItems = action.payload.items || [];
            // Deduplicate: only append items not already in the list
            const existingIds = new Set(state.workItems.map((item: any) => item.id || item.project_id));
            const uniqueNewItems = newItems.filter((item: any) => !existingIds.has(item.id || item.project_id));
            state.workItems = [...state.workItems, ...uniqueNewItems]
            state.workItemsTotalItems = action.payload.total || 0
            state.workItemsTotalPages = action.payload.pages || 1
            state.workItemsPage = action.payload.page || 1
            state.workItemsPerPage = action.payload.per_page || 10
            state.workItemsHasMore = state.workItemsPage < state.workItemsTotalPages
        },
        setWorkItemsLoading: (state, action) => {
            state.workItemsLoading = action.payload
        },
        setWorkItemsLoadingMore: (state, action) => {
            state.workItemsLoadingMore = action.payload
        },
        clearWorkItemsData: (state) => {
            state.workItems = []
            state.workItemsPage = 1
            state.workItemsHasMore = true
            state.workItemsTotalItems = 0
            state.workItemsTotalPages = 1
        },
        setWorkItemsSearchTerm: (state, action) => {
            state.workItemsSearchTerm = action.payload
            state.workItemsPage = 1
            state.workItemsHasMore = true
        },
        setSearchTerm: (state, action) => {
            state.searchTerm = action.payload
            state.page = 1
            state.hasMore = true
        },
        setWorkItemsFilters: (state, action: PayloadAction<{
            searchType?: 'all' | 'roadmap' | 'project' | 'epic' | 'feature';
            sortBy?: string;
            sourceFilter?: string;
            quarterFilter?: string[];
            pinnedFilter?: boolean;
        }>) => {
            if (action.payload.searchType !== undefined) {
                state.workItemsSearchType = action.payload.searchType;
            }
            if (action.payload.sortBy !== undefined) {
                state.workItemsSortBy = action.payload.sortBy;
            }
            if (action.payload.sourceFilter !== undefined) {
                state.workItemsSourceFilter = action.payload.sourceFilter;
            }
            if (action.payload.quarterFilter !== undefined) {
                state.workItemsQuarterFilter = action.payload.quarterFilter;
            }
            if (action.payload.pinnedFilter !== undefined) {
                state.workItemsPinnedFilter = action.payload.pinnedFilter;
            }
        },
        setOrganizationOnboardSettings: (state, action) => {
            state.onboardSettings = action.payload
        },
        setOnboardSettingsLoaded: (state, action: PayloadAction<boolean>) => {
            state.onboardSettingsLoaded = action.payload === true;
        },
        setOrgDetails: (state, action) => {
            state.orgDetails = action.payload || {}
        },
        setCreateOrganizationData: (state, action) => {
            state.orgdata = action.payload
        },
        setOrganizationLoading: (state, action) => {
            state.loading = action.payload
        },
        setOrganizationLoadingMore: (state, action) => {
            state.loadingMore = action.payload
        },
        setOrganizationError: (state, action) => {
            state.error = action.payload
        },
        resetOrganizationPagination: (state) => {
            state.page = 1
            state.hasMore = true
        },
        setOrganizationSkillData: (state, action) => {
            state.skills = action.payload.skills
        },
        setPinnedProjectsData: (state, action) => {
            state.pinnedProjects = action.payload.projects
            state.pinnedProjectsIds = action.payload.projects.map((project: GetPinnedProjectsResponse) => project.id)
        },
        
        setOrganizationSkillLoading: (state, action) => {
            state.skillLoading = action.payload
        },
        setRecentProjectsLoading: (state, action) => {
            state.recentProjectsLoading = action.payload
        },
        setOrganizationSkillError: (state, action) => {
            state.skillError = action.payload
        },
        setRecentProjectsError: (state, action) => {
            state.recentProjectsError = action.payload
            state.recentProjects = []
        },
        setOrganizationUpdateError: (state, action) => {
            state.orgUpdateError = action.payload
        },
        setOrganizationUpdateSuccess: (state, action) => {
            state.orgUpdateSuccess = action.payload
        },
        setOrganizationUpdateLoading: (state, action) => {
            state.orgUpdateLoading = action.payload
        },
        setShowSkillEdit: (state, action) => {
            state.showSkillEdit = action.payload
        },
        setInviteUsersLoading: (state, action) => {
            state.inviteUsersLoading = action.payload
        },
        setInviteUsersError: (state, action) => {
            state.inviteUsersError = action.payload
        },
        setInviteUsersSuccess: (state, action) => {
            state.inviteUsersSuccess = action.payload
        },
        setJiraSettingsGetSuccess: (state, action) => {
            state.jiraSettingsSuccess = action.payload
        },
        setJiraSettingsGetError: (state, action) => {
            state.jiraSettingsError = action.payload
        },
        setJiraSettingsLoading: (state, action) => {
            state.jiraSettingsLoading = action.payload
        },
        setJiraSettingsSaveSuccess: (state, action) => {
            state.jiraSettingsError = action.payload
        },
        setJiraSettingsSaveError: (state, action) => {
            state.jiraSettingsSaveError = action.payload
        },
        setProjectReferenceSaveError: (state, action) => {
            state.projectReferenceError = action.payload
        },
        setProjectReferenceLoading: (state, action) => {
            state.projectReferenceLoading = action.payload
        },
        projectReferenceRes: (state, action) => {
            state.projectReferenceData = action?.payload?.projects
        },
        setJiraSettings: (state, action) => {
            state.jiraSettings = action.payload
        },
        setAsanaSettings: (state, action) => {
            state.asanaSettings = action.payload
        },
        setAsanaSettingsGetSuccess: (state, action) => {
            state.asanaSettingsSuccess = action.payload
        },
        setAsanaSettingsGetError: (state, action) => {
            state.asanaSettingsError = action.payload
        },
        setAsanaSettingsLoading: (state, action) => {
            state.asanaSettingsLoading = action.payload
        },
        setAsanaSettingsSaveSuccess: (state, action) => {
            state.asanaSettingsError = action.payload
        },
        setAsanaSettingsSaveError: (state, action) => {
            state.asanaSettingsSaveError = action.payload
        },
        setOrgPreferencesLoading: (state, action) => {
            state.orgPreferencesLoading = action.payload
        },
        orgPreferencesRes: (state, action: PayloadAction<Preference[]>) => {
            state.orgPreferences = action.payload
        },
        addOrgPreference: (state, action: PayloadAction<Preference>) => {
            state.orgPreferences.push(action.payload);
        },
        removeOrgPreference: (state, action: PayloadAction<number>) => {
            state.orgPreferences = state.orgPreferences.filter(
                (p) => p.pref_id !== action.payload
            );
        },
        updatePreferenceId: (state, action: PayloadAction<{
            oldId: number; newId: number
        }>) => {
            const { oldId, newId } = action.payload;
            const preference = state.orgPreferences.find(el => el.pref_id === oldId);
            if (preference) {
                preference.pref_id = newId;
            }
        },
        setOrgPreferencesError: (state, action) => {
            state.orgPreferencesError = action.payload
        },
        setOrgWorkingDays: (state, action) => {
            state.orgWorkingDays = action.payload
        },
        setOrgWorkingDaysLoading: (state, action) => {
            state.orgWorkingDaysLoading = action.payload
        },
        setAzureSettingsGetSuccess: (state, action) => {
            state.azureSettingsSuccess = action.payload
        },
        setAzureSettingsGetError: (state, action) => {
            state.azureSettingsError = action.payload
        },
        setAzureSettingsLoading: (state, action) => {
            state.azureSettingsLoading = action.payload
        },
        setAzureSettingsSaveSuccess: (state, action) => {
            state.azureSettingsError = action.payload
        },
        setAzureSettingsSaveError: (state, action) => {
            state.azureSettingsSaveError = action.payload
        },
        setAzureSettings: (state, action) => {
            state.azureSettings = action.payload
        },
        setMondaySettingsGetSuccess: (state, action) => {
            state.mondaySettingsSuccess = action.payload
        },
        setMondaySettingsGetError: (state, action) => {
            state.mondaySettingsError = action.payload
        },
        setMondaySettingsLoading: (state, action) => {
            state.mondaySettingsLoading = action.payload
        },
        setMondaySettingsSaveSuccess: (state, action) => {
            state.mondaySettingsError = action.payload
        },
        setMondaySettingsSaveError: (state, action) => {
            state.mondaySettingsSaveError = action.payload
        },
        setMondaySettings: (state, action) => {
            state.mondaySettings = action.payload
        },
        setOrgScheduleData: (state, action) => {
            state.orgSchedule = action.payload.holidays
        },
        setOrgScheduleLoading: (state, action) => {
            state.orgScheduleLoading = action.payload
        },
        setOrgScheduleError: (state, action) => {
            state.orgScheduleError = action.payload
        },
        setshoworgScheduleEdit: (state, action) => {
            state.showOrgScheduleEdit = action.payload
        },
        setActiveIntegrationsLoading: (state, action: PayloadAction<boolean>) => {
            state.activeIntegrationsLoading = action.payload;
        },
        setActiveIntegrations: (state, action) => {
            state.activeIntegrations = action.payload;
        },
        setTeamsLoading: (state, action) => {
            state.teamsLoading = action.payload;
            // When we start loading, mark as "attempted" to prevent duplicate calls
            if (action.payload === true) {
                state.teamsFetched = true;
            }
        },
        setTeamsError: (state, action) => {
            // Only store serializable error information
            state.teamsError = action.payload?.message || action.payload || null;
            // Reset teamsFetched on error so it can be retried
            state.teamsFetched = false;
        },
        setTeams: (state, action) => {
            state.teams = action.payload;
            state.teamsFetched = true;
        },
        resetTeamsFetched: (state) => {
            state.teamsFetched = false;
        },
        setPendingInvitesLoading: (state, action) => {
            state.pendingInvitesLoading = action.payload;
        },
        setPendingInvitesError: (state, action) => {
            state.pendingInvitesError = action.payload;
        },
        setPendingInvites: (state, action) => {
            state.pendingInvites = action.payload;
        },
        setRevokeInviteLoading: (state, action) => {
            state.revokeInviteLoading = action.payload;
        },
        setRevokeInviteError: (state, action) => {
            state.revokeInviteError = action.payload;
        },
        setRevokeInviteSuccess: (state, action) => {
            state.revokeInviteSuccess = action.payload;
        },
        setGithubSettings: (state, action) => {
            state.githubSettings = action.payload;
        },
        setGithubSettingsLoading: (state, action) => {
            state.githubSettingsLoading = action.payload;
        },
        setGithubSettingsError: (state, action) => {
            state.githubSettingsError = action.payload;
        },
        setUserPreferencesLoading: (state, action) => {
            state.userPreferencesLoading = action.payload;
        },
        setUserPreferences: (state, action) => {
            state.userPreferences = action.payload;
        },
        removeUserPreference: (state, action: PayloadAction<number>) => {
            state.userPreferences = state.userPreferences.filter(
                (pref) => pref.id !== action.payload
            );
        },
        setSlackSettingsLoading: (state, action: PayloadAction<boolean>) => {
            state.slackSettingsLoading = action.payload;
        },
        setSlackSettings: (state, action: PayloadAction<SlackSettings | null>) => {
            state.slackSettings = action.payload || null;
        },
        setSlackSettingsError: (state, action: PayloadAction<unknown>) => {
            state.slackSettingsError = action.payload;
        },
        // States sync flags
        setAzureStatesSyncRunning: (state, action: PayloadAction<boolean>) => {
            state.azureStatesSyncRunning = action.payload;
        },
        setJiraStatesSyncRunning: (state, action: PayloadAction<boolean>) => {
            state.jiraStatesSyncRunning = action.payload;
        },
        setToleranceModes: (state, action: PayloadAction<any[]>) => {
            state.toleranceModes = action.payload;
        },
        setToleranceModesLoading: (state, action: PayloadAction<boolean>) => {
            state.toleranceModesLoading = action.payload;
        },
        setToleranceModesError: (state, action: PayloadAction<any>) => {
            state.toleranceModesError = action.payload;
        }
    }
});

export const {
    setOrganizationData,
    appendOrganizationData,
    setOrganizationLoadingMore,
    resetOrganizationPagination,
    //Work Items actions
    setWorkItemsData,
    appendWorkItemsData,
    setWorkItemsLoading,
    setWorkItemsLoadingMore,
    clearWorkItemsData,
    setWorkItemsSearchTerm,
    setCreateOrganizationData,
    setOrganizationLoading,
    setOrganizationError,
    setOrganizationSkillLoading,
    setOrganizationSkillError,
    setOrganizationSkillData,
    setOrganizationUpdateError,
    setOrganizationUpdateSuccess,
    setOrganizationUpdateLoading,
    setShowSkillEdit,
    setRecentProjectsLoading,
    setPinnedProjectsData,
    setRecentProjectsError,
    setInviteUsersLoading,
    setInviteUsersError,
    setInviteUsersSuccess,
    setJiraSettingsGetSuccess,
    setJiraSettingsLoading,
    setJiraSettingsGetError,
    setJiraSettingsSaveSuccess,
    setJiraSettingsSaveError,
    setJiraSettings,
    setProjectReferenceSaveError,
    setProjectReferenceLoading,
    projectReferenceRes,
    setOrganizationOnboardSettings,
    setOnboardSettingsLoaded,
    setOrgDetails,
    setAsanaSettingsGetSuccess,
    setAsanaSettingsLoading,
    setAsanaSettingsGetError,
    setAsanaSettingsSaveSuccess,
    setAsanaSettingsSaveError,
    setAsanaSettings,
    setOrgPreferencesError,
    setOrgPreferencesLoading,
    orgPreferencesRes,
    setOrgWorkingDays,
    setOrgWorkingDaysLoading,
    setAzureSettingsGetSuccess,
    setAzureSettingsLoading,
    setAzureSettingsGetError,
    setAzureSettingsSaveSuccess,
    setAzureSettingsSaveError,
    setAzureSettings,
    setMondaySettingsGetSuccess,
    setMondaySettingsLoading,
    setMondaySettingsGetError,
    setMondaySettingsSaveSuccess,
    setMondaySettingsSaveError,
    setMondaySettings,
    setOrgScheduleData,
    setOrgScheduleLoading,
    setOrgScheduleError,
    setshoworgScheduleEdit,
    setSearchTerm,
    setWorkItemsFilters,
    setActiveIntegrations,
    setActiveIntegrationsLoading,
    setTeamsLoading,
    setTeamsError,
    setTeams,
    resetTeamsFetched,
    setPendingInvitesLoading,
    setPendingInvitesError,
    setPendingInvites,
    setRevokeInviteLoading,
    setRevokeInviteError,
    setRevokeInviteSuccess,
    addOrgPreference,
    removeOrgPreference,
    updatePreferenceId,
    setGithubSettings,
    setGithubSettingsLoading,
    setGithubSettingsError,
    setUserPreferencesLoading,
    setUserPreferences,
    removeUserPreference,
    setSlackSettingsLoading,
    setSlackSettings,
    setSlackSettingsError,
    setAzureStatesSyncRunning,
    setJiraStatesSyncRunning,
    setToleranceModes,
    setToleranceModesLoading,
    setToleranceModesError
} = organizationSlice.actions;

export default organizationSlice.reducer;